<script lang="ts">
  import type { Snippet } from 'svelte'
  import type { HTMLFormAttributes } from 'svelte/elements'
  import { X } from '@lucide/svelte'
  import Button from '../button/Button.svelte'

  type Props = {
    open?: boolean
    title: string
    onsubmit?: HTMLFormAttributes['onsubmit']
    body?: Snippet
    footer?: Snippet
  }

  let {
    open = $bindable(false),
    title,
    onsubmit,
    body,
    footer,
  }: Props = $props()

  function close() {
    open = false
  }

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) close()
  }

  $effect(() => {
    if (!open) return
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  })
</script>

{#snippet content()}
  <div class="modal-header">
    <h2 class="modal-title">{title}</h2>
    <Button onclick={close} variant="ghost" size="icon-sm"><X /></Button>
  </div>
  {#if body}
    <div class="modal-body">
      {@render body()}
    </div>
  {/if}
  {#if footer}
    <div class="modal-footer">
      {@render footer()}
    </div>
  {/if}
{/snippet}

{#if open}
  <div class="backdrop" onclick={handleBackdropClick} role="presentation">
    <form class="modal-content" {onsubmit}>
      {@render content()}
    </form>
  </div>
{/if}

<style lang="scss">
  .backdrop {
    align-items: center;
    background: rgba(0, 0, 0, 0.7);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 100;

    .modal-content {
      align-items: stretch;
      background: var(--background);
      border-radius: 1.5rem;
      border: 1px solid var(--grid-stroke);
      display: flex;
      flex-direction: column;
      width: 30rem;
      padding: 1rem;

      .modal-header {
        align-items: center;
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
        }
      }

      .modal-body {
        display: flex;
        flex-direction: column;
        font-size: 1rem;
        gap: 1rem;
        padding: 1rem 0;
      }

      .modal-footer {
        padding-top: 1rem;
        border-top: 1px solid var(--grid-stroke);
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        justify-content: flex-end;
      }
    }
  }
</style>
